// aoe-range.ts — AOE range-button wiring. Byte-faithful port of includes/js/aoe-range-init.js
// (XZ closure Phase C, 2026-06-13). Single tap/click on a `.use-ability-range` button runs a
// pre-flight dry-run check then starts AOE placement via window.aoeManager.
//
// Public global: setupAOERangeButtons (called by initializeSelectionHandlers in template.js +
// the encounter-map bundle's XZPositioning on every refresh). handleAOESingleClick stays
// module-local — keeping it a single stable reference is what lets the idempotent re-bind
// (remove-then-add) drop the prior listener. Deps resolve as runtime globals (see globals.d.ts).

import { browserAbilityPreconditionRefusal } from './ability-preconditions-browser';

const w = window;

// DELEGATED binding (was per-element until 2026-06-16). The old setupAOERangeButtons
// added a click listener to each `.use-ability-range` element it found at call time. That
// broke for client-rendered buttons: the unified creature card's Core Abilities banner (and
// the PC-sheet spell/menta panes) paint from casterCache and REPAINT on every caster_update
// with fresh DOM nodes — wiping the per-element listener. A button painted after the last
// XZ refresh / selection-handler init therefore did nothing on click (non-AOE buttons survived
// because they carry an inline onclick; only AOE buttons rely solely on this listener — e.g.
// Darkness Sphere). A single document-level delegated listener can't go stale, so it fixes the
// whole class regardless of when/how often the button is (re)rendered.
let aoeDelegationBound = false;
function bindAOEDelegationOnce() {
    if (aoeDelegationBound) return;
    aoeDelegationBound = true;
    document.addEventListener('click', (e) => {
        const btn = (e.target as HTMLElement | null)?.closest<HTMLElement>('.use-ability-range');
        if (!btn) return;
        void handleAOESingleClick({ currentTarget: btn });
    });
}

// Back-compat shim: initializeSelectionHandlers (template.js) and XZPositioning still call this
// on every refresh. It now just guarantees the one delegated listener is installed (idempotent) —
// no per-element rebinding needed.
function setupAOERangeButtons() {
    bindAOEDelegationOnce();
}

function handleAOESingleClick(e: { currentTarget: HTMLElement }): boolean {
    const btn = e.currentTarget;
    const inFt3dDock = btn.closest('[data-ft3d-actor-dock]') !== null;
    // A lair/creature sheet can be open beside the FT3D scene while the selected actor's real action bar
    // remains in the bottom dock. Match the caster-bearing sheet to that dock directly: plain AOEs have
    // no optional fold-up, so ftActionBarArmAoeChoice correctly returns false for them even though they
    // are valid placement launches. An unrelated catalog/legacy sheet still keeps the existing warning.
    const paneCasterId = String(btn.closest<HTMLElement>('[data-ft-caster-id]')?.dataset.ftCasterId || '').toUpperCase();
    const activeCasterId = String(window.ftGetActiveCasterId?.() || '').trim().toUpperCase();
    const activeCampaignView = document.querySelector('[data-ft3d-campaign-encounter-view].is-active') !== null;
    // The source card can become interactive one paint before the bottom Action Bar finishes swapping
    // to the selected summon. The active-caster cache and active FT3D view are already authoritative at
    // that point, so do not reject the click merely because the dock DOM is one frame behind.
    const activeCasterSheet = !inFt3dDock && activeCampaignView
        && paneCasterId !== '' && paneCasterId === activeCasterId;
    const controlledSheet = !inFt3dDock && paneCasterId !== ''
        && Array.from(document.querySelectorAll<HTMLElement>(
            '[data-ft3d-actor-dock] [data-ft-caster-id]',
        )).some(node => String(node.dataset.ftCasterId || '').toUpperCase() === paneCasterId);
    const isFt3d = inFt3dDock || activeCasterSheet || controlledSheet;
    if (!isFt3d) {
        window.ftActionBarClearChoice?.();
        displayWarning('Open the FT3D campaign view to place an area ability.');
        return false;
    }
    // No server pre-check (owner 2026-09-10 — use_ability_pre_check_inc.php is retired). The local gate refuses only
    // what the cached rows prove PHP would refuse, with PHP's message; Janus re-runs every pre-condition at cast.
    // variable_1/2 mirror what FT3DAOEPlacement.cast() will post, so the gate judges the same request.
    const abilityId = String(btn.dataset.ability_id || '').trim().toUpperCase();
    const spellType = btn.dataset.spell_type || '';
    const inputIdReference = btn.dataset.input_id_reference || '';
    const inputValue = inputIdReference ? ((document.getElementById(inputIdReference) as HTMLInputElement | null)?.value || '') : '';
    const nestedInventoryId = (btn.dataset.nestedInventoryId || '').trim();
    const sangSelKey = `select[data-ft-sanguine-dmg-for="${abilityId}"]`;
    const sangPick = String((document.querySelector(`.ft-action-choicebar ${sangSelKey}`) as HTMLSelectElement | null)?.value
        ?? (document.querySelector(sangSelKey) as HTMLSelectElement | null)?.value ?? '').trim();
    let variable1: string | undefined;
    let variable2: string | undefined;
    // A charged cast posts its source item as variable_2 (FT3DAOEPlacement.cast), so the gate judges that row's charges.
    const chargedSource = (spellType === 'charged_spell' || spellType === 'charged_ability') ? String(btn.dataset.source || '').trim() : '';
    if (nestedInventoryId && checkIfValidUUID(nestedInventoryId)) { variable1 = 'nested_inventory_source'; variable2 = nestedInventoryId; }
    else if (chargedSource && checkIfValidUUID(chargedSource)) { variable1 = spellType; variable2 = chargedSource; }
    else if (sangPick && !spellType) { variable1 = inputValue; variable2 = sangPick; }
    else if (spellType && inputIdReference) { variable1 = spellType; variable2 = inputValue; }
    else if (spellType) variable1 = spellType;
    else if (inputIdReference) variable1 = inputValue;
    let ability: unknown = null;
    try { ability = w.ftFindFullNestedItem?.(abilityId) ?? null; } catch { ability = null; }
    const refusal = browserAbilityPreconditionRefusal({
        casterId: paneCasterId || activeCasterId, encounterId: String(window.ftGetActiveEncounterId?.() || '').trim().toUpperCase(),
        ability: ability ?? w.FateTide?.objectCache?.get(abilityId) ?? null, variable1, variable2,
    });
    if (refusal) { window.ftActionBarClearChoice?.(); displayWarning(refusal); return false; }
    if (window.aoeManager?.ft3dActive) {
        // Availability can change while aiming; check it before re-click confirms or replaces a draft.
        const liveIntent = (window.aoeManager.ft3dActive as { envelope?: { intent?: { abilityId?: string; casterId?: string } | null } })?.envelope?.intent;
        if (liveIntent && String(liveIntent.abilityId || '').toUpperCase() === abilityId
            && (!paneCasterId || String(liveIntent.casterId || '').toUpperCase() === paneCasterId)) {
            window.aoeManager.confirmFt3d();
            return true;
        }
        window.aoeManager.cancelFt3d();
    }
    if (typeof window.aoeManager?.startFt3d !== 'function') {
        window.ftActionBarClearChoice?.();
        displayWarning('Area placement is not available yet. Open the FT3D campaign view and choose the ability again.');
        return false;
    }
    // Only an available click may open options or enter area placement.
    window.ftActionBarArmAoeChoice?.(btn);
    window.aoeManager.startFt3d(btn);
    return !!window.aoeManager.ft3dActive;
}

// WINDOW EXPORTS
Object.assign(w, { setupAOERangeButtons, ftStartAoePlacement: (button: HTMLElement) => handleAOESingleClick({ currentTarget: button }) });

// Install the delegated listener immediately at bundle load, so AOE buttons are live even
// before the first initializeSelectionHandlers / XZ refresh runs.
bindAOEDelegationOnce();

export {};
